import { useState, useEffect, useCallback } from "react";
import api from "@/utils/api";
import { formatApiError, isAuthError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MultiSearchableSelect, SearchableSelect } from "@/components/SearchableSelect";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Trash2, ChevronLeft, ChevronRight, Calendar, List, Users, UserPlus, MapPin, Clock, User, GraduationCap, CalendarX2, Copy, CalendarDays } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths, isSameMonth, isSameDay, parseISO, startOfWeek, endOfWeek, addDays, subDays } from "date-fns";
import ExportButtons from "@/components/ExportButtons";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { useBranding } from "@/contexts/BrandingContext";
import MemberProfile from "@/components/MemberProfile";

// Time-slot helpers for the weekly calendar (9:00 → 17:00 in 30-min intervals)
const DAY_START_HOUR = 9;
const DAY_END_HOUR = 17;
const SLOT_MINUTES = 30;
const SLOT_HEIGHT = 28; // px per 30-minute slot
const TIME_SLOTS = (() => {
  const slots = [];
  for (let h = DAY_START_HOUR; h < DAY_END_HOUR; h++) {
    for (let m = 0; m < 60; m += SLOT_MINUTES) {
      slots.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return slots;
})();

const toMinutes = (hhmm) => {
  if (!hhmm) return 0;
  const [h, m] = hhmm.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};

const formatDuration = (start, end) => {
  const mins = toMinutes(end) - toMinutes(start);
  if (mins <= 0) return "—";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h && m) return `${h}h ${m}m (${mins} min)`;
  if (h) return `${h}h (${mins} min)`;
  return `${m} min`;
};

const STATUS_COLORS = { scheduled: "bg-sky-100 text-sky-700", in_progress: "bg-amber-100 text-amber-700", completed: "bg-emerald-100 text-emerald-700", cancelled: "bg-red-100 text-red-700" };
const CAT_COLORS = {
  "Domestic Gas": "bg-blue-500", "Commercial Gas": "bg-indigo-500", "Electrical": "bg-amber-500",
  "Oil": "bg-orange-500", "Plumbing": "bg-cyan-500", "Low Carbon & Net Zero": "bg-emerald-500",
  "Retrofit & Compliance": "bg-violet-500", "ECO & Energy Efficiency": "bg-teal-500",
};
const CAT_BORDER = {
  "Domestic Gas": "border-blue-500", "Commercial Gas": "border-indigo-500", "Electrical": "border-amber-500",
  "Oil": "border-orange-500", "Plumbing": "border-cyan-500", "Low Carbon & Net Zero": "border-emerald-500",
  "Retrofit & Compliance": "border-violet-500", "ECO & Energy Efficiency": "border-teal-500",
};
const CAT_BG_LIGHT = {
  "Domestic Gas": "bg-blue-50", "Commercial Gas": "bg-indigo-50", "Electrical": "bg-amber-50",
  "Oil": "bg-orange-50", "Plumbing": "bg-cyan-50", "Low Carbon & Net Zero": "bg-emerald-50",
  "Retrofit & Compliance": "bg-violet-50", "ECO & Energy Efficiency": "bg-teal-50",
};
const CAT_TEXT = {
  "Domestic Gas": "text-blue-700", "Commercial Gas": "text-indigo-700", "Electrical": "text-amber-700",
  "Oil": "text-orange-700", "Plumbing": "text-cyan-700", "Low Carbon & Net Zero": "text-emerald-700",
  "Retrofit & Compliance": "text-violet-700", "ECO & Energy Efficiency": "text-teal-700",
};

export default function CoursesPage() {
  useDocumentTitle("Courses & Calendar", "Schedule courses, manage capacity and trainer availability.");
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [quals, setQuals] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("calendar");
  const [calendarMode, setCalendarMode] = useState("month"); // "month" | "week" | "multi"
  const [hideWeekends, setHideWeekends] = useState(true); // default ON — most courses run weekdays
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [qualFilter, setQualFilter] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [enrollCourse, setEnrollCourse] = useState(null);
  const [enrollMemberIds, setEnrollMemberIds] = useState([]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailCourse, setDetailCourse] = useState(null);
  const [profileMemberId, setProfileMemberId] = useState(null);
  const [daysDialogEnrol, setDaysDialogEnrol] = useState(null); // {id, member_name, attending_dates}
  const [daysDialogPick, setDaysDialogPick] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [trainerDialogOpen, setTrainerDialogOpen] = useState(false);
  const [editingTrainer, setEditingTrainer] = useState(null);
  const [trainerForm, setTrainerForm] = useState({ name: "", email: "", phone: "", specializations: [], notes: "" });
  const [unavailOpen, setUnavailOpen] = useState(false);
  const [unavailTrainer, setUnavailTrainer] = useState(null);
  const [unavailForm, setUnavailForm] = useState({ start_date: "", end_date: "", reason: "Holiday" });
  const [selectedDays, setSelectedDays] = useState([]); // ISO date strings selected on the calendar
  const [bulkUnavailOpen, setBulkUnavailOpen] = useState(false);
  const [bulkUnavailForm, setBulkUnavailForm] = useState({ trainer_id: "", reason: "Holiday" });
  // Dialog for the "+N more" overflow on the month calendar — lists every
  // course scheduled for a single day so Jamie can jump to any of them.
  const [dayExpand, setDayExpand] = useState(null); // { date: "YYYY-MM-DD", courses: [...] }
  // Inline-edit state for enrollment notes in the course detail dialog.
  const [editingNoteFor, setEditingNoteFor] = useState(null); // enrollment id
  const [noteDraft, setNoteDraft] = useState("");
  const DEFAULT_LOCATION = useBranding().default_course_location || "";
  const emptyForm = { qualification_id: "", title: "", start_date: "", end_date: "", start_time: "09:00", end_time: "17:00", location: DEFAULT_LOCATION, max_capacity: 6, walk_in_count: 0, instructor: "", status: "scheduled", price_per_student: 0, course_cost: 0, vat_applicable: false, notes: "" };
  const [form, setForm] = useState(emptyForm);

  const fetchCourses = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (qualFilter) params.set("qualification_id", qualFilter);
      const { data } = await api.get(`/courses?${params}&limit=500`);
      setCourses(data.courses);
    } catch (err) { if (!isAuthError(err)) toast.error("Failed to load courses"); }
    finally { setLoading(false); }
  }, [qualFilter]);

  const fetchTrainers = useCallback(async () => {
    try { const { data } = await api.get("/courses/trainers"); setTrainers(data); } catch (err) { console.error("Failed to load trainers:", err); }
  }, []);

  useEffect(() => {
    fetchCourses();
    api.get("/qualifications").then(r => setQuals(r.data)).catch(() => {});
    fetchTrainers();
  }, [fetchCourses, fetchTrainers]);

  function openCreate(prefilDate = "") {
    setForm({ ...emptyForm, start_date: prefilDate, end_date: prefilDate });
    setEditing(null);
    setDialogOpen(true);
  }
  const openEdit = (c) => { setEditing(c); setForm({ qualification_id: c.qualification_id, title: c.title, start_date: c.start_date, end_date: c.end_date, start_time: c.start_time || "09:00", end_time: c.end_time || "17:00", location: c.location || "", max_capacity: c.max_capacity || 6, walk_in_count: c.walk_in_count || 0, instructor: c.instructor || "", status: c.status, price_per_student: c.price_per_student || 0, course_cost: c.course_cost || 0, vat_applicable: !!c.vat_applicable, notes: c.notes || "" }); setDialogOpen(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await api.put(`/courses/${editing.id}`, form); toast.success("Course updated"); }
      else { await api.post("/courses", form); toast.success("Course created"); }
      setDialogOpen(false); fetchCourses();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const handleDelete = (c) => {
    // window.confirm() is blocked inside many iframe sandboxes — use a
    // state-driven AlertDialog instead so the prompt actually appears.
    setDeleteTarget(c);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/courses/${deleteTarget.id}`);
      toast.success(`"${deleteTarget.title}" deleted`);
      setDeleteTarget(null);
      fetchCourses();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setDeleteTarget(null);
    }
  };

  const openEnroll = async (c) => {
    setEnrollCourse(c); setEnrollMemberIds([]);
    try {
      // limit=10000 + role=student so the dropdown truly contains every
      // enrollable learner (previous default of 500 truncated for larger
      // orgs, hiding recently-added members).
      const { data } = await api.get("/members?limit=10000&role=student");
      setMembers(data.members);
    } catch (err) { console.error("Failed to load members:", err); }
    setEnrollOpen(true);
  };

  const viewCourseDetail = async (c) => {
    try {
      const [courseRes, wlRes] = await Promise.all([
        api.get(`/courses/${c.id}`),
        api.get(`/courses/${c.id}/waitlist`),
      ]);
      setDetailCourse({ ...courseRes.data, waitlist: wlRes.data });
      setDetailOpen(true);
    } catch { toast.error("Failed to load course details"); }
  };

  const refreshDetail = async () => {
    if (!detailCourse) return;
    try {
      const [courseRes, wlRes] = await Promise.all([api.get(`/courses/${detailCourse.id}`), api.get(`/courses/${detailCourse.id}/waitlist`)]);
      setDetailCourse({ ...courseRes.data, waitlist: wlRes.data });
    } catch { /* keep current view */ }
  };

  const handleRemoveEnrollment = async (courseId, enrollmentId) => {    try {
      await api.delete(`/courses/${courseId}/enrollments/${enrollmentId}`);
      toast.success("Enrollment removed");
      const { data } = await api.get(`/courses/${detailCourse.id}`);
      setDetailCourse(data);
      fetchCourses();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openDaysDialog = (enr) => {
    setDaysDialogEnrol(enr);
    setDaysDialogPick(enr.attending_dates || detailCourse?.course_days || []);
  };

  const toggleDayPick = (d) => {
    setDaysDialogPick(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d].sort());
  };

  const saveAttendingDates = async () => {
    if (!daysDialogEnrol || !daysDialogPick.length) {
      toast.error("Pick at least one day");
      return;
    }
    try {
      await api.put(`/courses/${detailCourse.id}/enrollments/${daysDialogEnrol.id}`, {
        attending_dates: daysDialogPick,
      });
      toast.success("Attending days updated");
      setDaysDialogEnrol(null);
      const { data } = await api.get(`/courses/${detailCourse.id}`);
      setDetailCourse(data);
      fetchCourses();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openCreateTrainer = () => { setEditingTrainer(null); setTrainerForm({ name: "", email: "", phone: "", specializations: [], notes: "" }); setTrainerDialogOpen(true); };
  const openEditTrainer = (t) => { setEditingTrainer(t); setTrainerForm({ name: t.name, email: t.email || "", phone: t.phone || "", specializations: t.specializations || [], notes: t.notes || "" }); setTrainerDialogOpen(true); };
  const handleTrainerSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTrainer) { await api.put(`/courses/trainers/${editingTrainer.id}`, trainerForm); toast.success("Trainer updated"); }
      else { await api.post("/courses/trainers", trainerForm); toast.success("Trainer added"); }
      setTrainerDialogOpen(false); fetchTrainers();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const handleDeleteTrainer = async (t) => {
    if (!window.confirm(`Remove trainer ${t.name}?`)) return;
    try { await api.delete(`/courses/trainers/${t.id}`); toast.success("Trainer removed"); fetchTrainers(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const handleDuplicate = async (c) => {
    try {
      const { data } = await api.post(`/courses/${c.id}/duplicate`);
      toast.success(`Course duplicated — update the dates`);
      fetchCourses();
      openEdit(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const handleAddToWaitlist = async (courseId, memberId) => {
    try {
      await api.post(`/courses/${courseId}/waitlist`, { member_id: memberId });
      toast.success("Added to waiting list");
      const { data } = await api.get(`/courses/${courseId}`);
      const wl = await api.get(`/courses/${courseId}/waitlist`);
      setDetailCourse({ ...data, waitlist: wl.data });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const handleRemoveFromWaitlist = async (courseId, waitlistId) => {
    try {
      await api.delete(`/courses/${courseId}/waitlist/${waitlistId}`);
      toast.success("Removed from waiting list");
      const { data } = await api.get(`/courses/${courseId}`);
      const wl = await api.get(`/courses/${courseId}/waitlist`);
      setDetailCourse({ ...data, waitlist: wl.data });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openUnavail = (t) => { setUnavailTrainer(t); setUnavailForm({ start_date: "", end_date: "", reason: "Holiday" }); setUnavailOpen(true); };
  const handleAddUnavail = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/courses/trainers/${unavailTrainer.id}/unavailability`, unavailForm);
      toast.success("Unavailability added");
      setUnavailOpen(false); fetchTrainers();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const handleRemoveUnavail = async (unavailId) => {
    try { await api.delete(`/courses/trainers/unavailability/${unavailId}`); toast.success("Removed"); fetchTrainers(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  // --- Calendar bulk selection ---
  const toggleDaySelection = (day, e) => {
    const iso = format(day, "yyyy-MM-dd");
    const shift = e?.shiftKey;
    setSelectedDays(prev => {
      if (shift && prev.length > 0) {
        // Range select from last picked to this
        const last = parseISO(prev[prev.length - 1]);
        const start = last <= day ? last : day;
        const end = last <= day ? day : last;
        const range = eachDayOfInterval({ start, end }).map(d => format(d, "yyyy-MM-dd"));
        // Merge with existing (deduped, sorted)
        const merged = Array.from(new Set([...prev, ...range])).sort();
        return merged;
      }
      return prev.includes(iso) ? prev.filter(x => x !== iso) : [...prev, iso].sort();
    });
  };
  const clearSelection = () => setSelectedDays([]);

  const openBulkUnavail = () => {
    if (selectedDays.length === 0) return;
    setBulkUnavailForm({ trainer_id: "", reason: "Holiday" });
    setBulkUnavailOpen(true);
  };

  const submitBulkUnavail = async (e) => {
    e.preventDefault();
    if (!bulkUnavailForm.trainer_id || selectedDays.length === 0) return;
    const start_date = selectedDays[0];
    const end_date = selectedDays[selectedDays.length - 1];
    try {
      await api.post(`/courses/trainers/${bulkUnavailForm.trainer_id}/unavailability`, {
        start_date,
        end_date,
        reason: bulkUnavailForm.reason || "Holiday",
      });
      toast.success(`Marked unavailable: ${start_date} → ${end_date}`);
      setBulkUnavailOpen(false);
      setSelectedDays([]);
      fetchTrainers();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const createCourseForSelection = () => {
    if (selectedDays.length === 0) return;
    const start = selectedDays[0];
    const end = selectedDays[selectedDays.length - 1];
    setEditing(null);
    setForm({ ...emptyForm, start_date: start, end_date: end });
    setDialogOpen(true);
  };

  // Interactive trainer availability: toggle a single day Free <-> Off
  // Strict precedence: Teaching > Off > Free (a teaching day stays Teaching even if also flagged off)
  const toggleTrainerDay = async (trainer, dayStr, isBusy, isUnavail) => {
    if (isBusy) {
      toast.info("Trainer is teaching on this day — edit the course to change.");
      return;
    }
    try {
      if (isUnavail) {
        // Find unavailability records covering this day
        const matching = (trainer.unavailability || []).filter(u => u.start_date <= dayStr && dayStr <= u.end_date);
        for (const u of matching) {
          if (u.start_date === dayStr && u.end_date === dayStr) {
            await api.delete(`/courses/trainers/unavailability/${u.id}`);
          } else if (u.start_date === dayStr) {
            // Trim start by 1 day
            const newStart = format(addDays(parseISO(dayStr), 1), "yyyy-MM-dd");
            await api.delete(`/courses/trainers/unavailability/${u.id}`);
            await api.post(`/courses/trainers/${trainer.id}/unavailability`, { start_date: newStart, end_date: u.end_date, reason: u.reason });
          } else if (u.end_date === dayStr) {
            const newEnd = format(subDays(parseISO(dayStr), 1), "yyyy-MM-dd");
            await api.delete(`/courses/trainers/unavailability/${u.id}`);
            await api.post(`/courses/trainers/${trainer.id}/unavailability`, { start_date: u.start_date, end_date: newEnd, reason: u.reason });
          } else {
            // Split into two
            const before_end = format(subDays(parseISO(dayStr), 1), "yyyy-MM-dd");
            const after_start = format(addDays(parseISO(dayStr), 1), "yyyy-MM-dd");
            await api.delete(`/courses/trainers/unavailability/${u.id}`);
            await api.post(`/courses/trainers/${trainer.id}/unavailability`, { start_date: u.start_date, end_date: before_end, reason: u.reason });
            await api.post(`/courses/trainers/${trainer.id}/unavailability`, { start_date: after_start, end_date: u.end_date, reason: u.reason });
          }
        }
        toast.success(`${trainer.name}: marked Free on ${dayStr}`);
      } else {
        await api.post(`/courses/trainers/${trainer.id}/unavailability`, { start_date: dayStr, end_date: dayStr, reason: "Holiday" });
        toast.success(`${trainer.name}: marked Off on ${dayStr}`);
      }
      fetchTrainers();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const handleEnroll = async () => {
    if (!enrollMemberIds.length) return;
    let enrolled = 0, full = 0;
    const fullIds = [];
    const failures = [];
    for (const mid of enrollMemberIds) {
      try {
        await api.post(`/courses/${enrollCourse.id}/enroll`, { member_id: mid });
        enrolled += 1;
      } catch (err) {
        const detail = formatApiError(err.response?.data?.detail) || (err.response ? `Error ${err.response.status}` : "Network error — check your connection");
        if (String(detail).includes("full")) { full += 1; fullIds.push(mid); }
        else { failures.push(`${members.find((m) => m.id === mid)?.name || "Learner"}: ${detail}`); }
      }
    }
    if (enrolled) toast.success(`${enrolled} learner${enrolled > 1 ? "s" : ""} enrolled`);
    if (failures.length) toast.error(`${failures.length} enrolment${failures.length > 1 ? "s" : ""} failed`, { description: failures.join("\n"), duration: 12000 });
    if (full && fullIds.length) {
      if (window.confirm(`Course is full for ${full} learner(s). Add them to the waiting list?`)) {
        for (const mid of fullIds) {
          try { await handleAddToWaitlist(enrollCourse.id, mid); } catch (e) { /* swallow */ }
        }
      }
    }
    setEnrollOpen(false); fetchCourses();
  };

  const isStaff = user?.role === "admin" || user?.role === "staff" || user?.role === "superadmin";

  // Calendar helpers
  const isWeekMode = calendarMode === "week";
  const isMultiMode = calendarMode === "multi";
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const weekStart = startOfWeek(currentMonth, { weekStartsOn: 0 });
  const weekEnd = endOfWeek(currentMonth, { weekStartsOn: 0 });
  const days = isWeekMode
    ? eachDayOfInterval({ start: weekStart, end: weekEnd })
    : eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startPad = isWeekMode ? 0 : getDay(monthStart);
  const padDays = Array(startPad).fill(null);

  // Multi-month: build a second consecutive month
  const nextMonthStart = startOfMonth(addMonths(currentMonth, 1));
  const nextMonthEnd = endOfMonth(addMonths(currentMonth, 1));
  const daysNext = eachDayOfInterval({ start: nextMonthStart, end: nextMonthEnd });
  const startPadNext = getDay(nextMonthStart);
  const padDaysNext = Array(startPadNext).fill(null);

  // Hide weekends helpers
  const isWeekend = (d) => { const dow = getDay(d); return dow === 0 || dow === 6; };
  const visibleDayOfWeek = (i) => hideWeekends ? (i !== 0 && i !== 6) : true;
  const weekHeaders = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const goPrev = () => setCurrentMonth(isWeekMode ? subDays(currentMonth, 7) : subMonths(currentMonth, 1));
  const goNext = () => setCurrentMonth(isWeekMode ? addDays(currentMonth, 7) : addMonths(currentMonth, 1));
  const headerLabel = isWeekMode
    ? `${format(weekStart, "MMM d")} – ${format(weekEnd, "MMM d, yyyy")}`
    : isMultiMode
      ? `${format(currentMonth, "MMM")} – ${format(addMonths(currentMonth, 1), "MMM yyyy")}`
      : format(currentMonth, "MMMM yyyy");

  const getCoursesForDay = (day) => {
    // Show a course on EVERY day it spans (start_date through end_date
    // inclusive) — previously this only matched start_date which made
    // multi-day courses look like single-day events.
    const dayIso = format(day, "yyyy-MM-dd");
    return courses.filter(c => {
      const s = c.start_date || "";
      const e = c.end_date || s;
      return s && dayIso >= s && dayIso <= e;
    });
  };

  // For a given course + day, return {dayNum, totalDays} so multi-day
  // courses can render "Day 2 of 3" in the tile.
  const dayInfo = (course, day) => {
    if (!course.start_date) return null;
    const start = parseISO(course.start_date);
    const end = parseISO(course.end_date || course.start_date);
    const totalDays = Math.round((end - start) / 86400000) + 1;
    if (totalDays <= 1) return null;
    const dayNum = Math.round((day - start) / 86400000) + 1;
    return { dayNum, totalDays };
  };

  return (
    <div className="space-y-4" data-testid="courses-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Tabs value={view} onValueChange={setView}>
            <TabsList className="h-8" data-testid="courses-view-tabs">
              <TabsTrigger value="calendar" className="text-xs px-3 py-1"><Calendar size={14} className="mr-1" />Calendar</TabsTrigger>
              <TabsTrigger value="list" className="text-xs px-3 py-1"><List size={14} className="mr-1" />List</TabsTrigger>
              <TabsTrigger value="trainers" className="text-xs px-3 py-1"><User size={14} className="mr-1" />Trainers</TabsTrigger>
            </TabsList>
          </Tabs>
          <SearchableSelect
            options={[{ value: "all", label: "All qualifications" }, ...quals.map(q => ({ value: q.id, label: q.name, hint: q.category }))]}
            value={qualFilter || "all"}
            onChange={(v) => setQualFilter(v === "all" || !v ? "" : v)}
            placeholder="All qualifications"
            searchPlaceholder="Search qualifications…"
            clearable={false}
            className="w-[220px]"
            testId="courses-qual-filter"
          />
        </div>
        {isStaff && (
          <div className="flex items-center gap-2">
            <ExportButtons endpoint="courses" label="Courses" />
            <Button onClick={openCreate} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-course-btn">
              <Plus size={16} className="mr-1" /> Add Course
            </Button>
          </div>
        )}
      </div>

      {/* Calendar View */}
      {view === "calendar" && (
        <div className="border border-border rounded-md bg-white" data-testid="course-calendar">
        <div className="flex items-center justify-between p-3 border-b border-border">
          <button onClick={goPrev} className="p-1.5 rounded hover:bg-slate-100" data-testid="cal-prev-month"><ChevronLeft size={18} /></button>
          <div className="flex items-center gap-3">
            <h3 className="font-heading font-semibold text-sm" data-testid="cal-header-label">{headerLabel}</h3>
            <Tabs value={calendarMode} onValueChange={setCalendarMode}>
              <TabsList className="h-7" data-testid="cal-mode-tabs">
                <TabsTrigger value="month" className="text-[11px] px-2.5 py-0.5" data-testid="cal-mode-month">Month</TabsTrigger>
                <TabsTrigger value="multi" className="text-[11px] px-2.5 py-0.5" data-testid="cal-mode-multi">2 Months</TabsTrigger>
                <TabsTrigger value="week" className="text-[11px] px-2.5 py-0.5" data-testid="cal-mode-week">Week</TabsTrigger>
              </TabsList>
            </Tabs>
            <label className="flex items-center gap-1 text-[11px] text-muted-foreground cursor-pointer" title="Hide Saturday and Sunday columns" data-testid="hide-weekends-toggle">
              <input type="checkbox" checked={hideWeekends} onChange={(e) => setHideWeekends(e.target.checked)} className="h-3 w-3 cursor-pointer" />
              <span>Hide weekends</span>
            </label>
            <button
              onClick={() => setCurrentMonth(new Date())}
              className="text-[11px] px-2 py-1 rounded border border-border hover:bg-slate-100 text-muted-foreground"
              data-testid="cal-today-btn"
            >
              Today
            </button>
          </div>
          <button onClick={goNext} className="p-1.5 rounded hover:bg-slate-100" data-testid="cal-next-month"><ChevronRight size={18} /></button>
        </div>
          {/* Calendar legend */}
          <div className="flex items-center gap-4 px-3 py-2 border-b border-border bg-slate-50/50 flex-wrap">
            <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Capacity:</span>
            <span className="flex items-center gap-1 text-[10px]"><span className="w-2 h-2 rounded-full bg-emerald-500" />Available</span>
            <span className="flex items-center gap-1 text-[10px]"><span className="w-2 h-2 rounded-full bg-amber-500" />Nearly Full</span>
            <span className="flex items-center gap-1 text-[10px]"><span className="w-2 h-2 rounded-full bg-red-500" />Full</span>
            {isStaff && (
              <span className="text-[10px] text-muted-foreground ml-auto italic hidden sm:inline">
                Click a day to add a course · Shift+click to select range · &quot;+&quot; to multi-select
              </span>
            )}
          </div>

          {/* Bulk selection action bar */}
          {isStaff && selectedDays.length > 0 && (
            <div className="flex items-center justify-between gap-2 px-3 py-2 bg-sky-50 border-b border-sky-200" data-testid="bulk-action-bar">
              <div className="text-xs text-sky-900">
                <strong>{selectedDays.length}</strong> day{selectedDays.length === 1 ? "" : "s"} selected
                {selectedDays.length > 1 && <span className="text-sky-700/70 ml-2">({selectedDays[0]} → {selectedDays[selectedDays.length - 1]})</span>}
              </div>
              <div className="flex items-center gap-1.5">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={createCourseForSelection} data-testid="bulk-add-course-btn">
                  <Plus size={12} className="mr-1" /> Add Course
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={openBulkUnavail} data-testid="bulk-unavailable-btn">
                  <CalendarX2 size={12} className="mr-1" /> Mark Trainer Unavailable
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={clearSelection} data-testid="bulk-clear-btn">
                  Clear
                </Button>
              </div>
            </div>
          )}
          {/* Week Time-Slot View (9am - 5pm, 30-min intervals) */}
          {isWeekMode && (
            <div className="overflow-x-auto" data-testid="week-timeslot-view">
              <div className="min-w-[800px]">
                {/* Day headers */}
                <div className="grid sticky top-0 bg-white z-10" style={{ gridTemplateColumns: `60px repeat(7, 1fr)` }}>
                  <div className="px-2 py-2 text-center text-[10px] font-medium text-muted-foreground border-b border-r border-border">Time</div>
                  {days.map(day => {
                    const isToday = isSameDay(day, new Date());
                    const dayIso = format(day, "yyyy-MM-dd");
                    const isSelected = selectedDays.includes(dayIso);
                    return (
                      <div key={day.toISOString()} className={`px-2 py-2 text-center border-b border-r border-border ${isToday ? "bg-sky-50/60" : ""} ${isSelected ? "bg-sky-100" : ""}`}>
                        <div className="text-[10px] text-muted-foreground">{format(day, "EEE")}</div>
                        <div className={`text-sm font-semibold ${isToday ? "text-sky-700" : ""}`}>{format(day, "d")}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Time grid */}
                <div className="grid relative" style={{ gridTemplateColumns: `60px repeat(7, 1fr)` }}>
                  {/* Time labels column */}
                  <div className="border-r border-border">
                    {TIME_SLOTS.map(slot => (
                      <div key={slot} style={{ height: SLOT_HEIGHT }} className="text-[10px] text-muted-foreground px-2 border-b border-border flex items-start pt-0.5">
                        {slot.endsWith(":00") ? slot : ""}
                      </div>
                    ))}
                  </div>

                  {/* Day columns */}
                  {days.map(day => {
                    const dayIso = format(day, "yyyy-MM-dd");
                    const isToday = isSameDay(day, new Date());
                    const isSelected = selectedDays.includes(dayIso);
                    const dayCourses = getCoursesForDay(day);
                    return (
                      <div
                        key={day.toISOString()}
                        className={`relative border-r border-border ${isToday ? "bg-sky-50/30" : ""} ${isSelected ? "bg-sky-50" : ""}`}
                        data-testid={`week-day-col-${dayIso}`}
                      >
                        {/* 30-min slot cells (click to pre-fill) */}
                        {TIME_SLOTS.map(slot => (
                          <button
                            key={slot}
                            onClick={(e) => {
                              if (!isStaff) return;
                              if (e.shiftKey || e.metaKey || e.ctrlKey || selectedDays.length > 0) {
                                toggleDaySelection(day, e);
                                return;
                              }
                              // Pre-fill date + start_time, end_time = start + 60 min by default
                              const [sh, sm] = slot.split(":").map(Number);
                              const endMins = sh * 60 + sm + 60;
                              const endH = Math.min(DAY_END_HOUR, Math.floor(endMins / 60));
                              const endM = endMins % 60;
                              const endTime = `${String(endH).padStart(2, "0")}:${String(endM).padStart(2, "0")}`;
                              setEditing(null);
                              setForm(f => ({ ...f, ...emptyForm, start_date: dayIso, end_date: dayIso, start_time: slot, end_time: endTime }));
                              setDialogOpen(true);
                            }}
                            style={{ height: SLOT_HEIGHT }}
                            className={`w-full border-b border-slate-100 hover:bg-sky-100/40 transition-colors ${slot.endsWith(":00") ? "border-b-slate-200" : ""}`}
                            data-testid={`week-slot-${dayIso}-${slot}`}
                          />
                        ))}
                        {/* Course blocks (absolute) */}
                        {dayCourses.map(c => {
                          const startMins = toMinutes(c.start_time || "09:00") - DAY_START_HOUR * 60;
                          const endMins = toMinutes(c.end_time || "17:00") - DAY_START_HOUR * 60;
                          const top = Math.max(0, (startMins / SLOT_MINUTES) * SLOT_HEIGHT);
                          const heightPx = Math.max(SLOT_HEIGHT - 2, ((endMins - startMins) / SLOT_MINUTES) * SLOT_HEIGHT - 2);
                          const enrolled = c.enrolled_count || 0;
                          const max = c.max_capacity || 6;
                          const isFull = enrolled >= max;
                          // For multi-day courses, show the per-day count on this specific day
                          const isMultiDay = (c.course_days || []).length > 1;
                          const dayCount = isMultiDay ? ((c.per_day_counts || {})[dayIso] ?? enrolled) : enrolled;
                          const borderColor = CAT_BORDER[c.qualification_category] || "border-slate-400";
                          const bgLight = CAT_BG_LIGHT[c.qualification_category] || "bg-slate-50";
                          const textColor = CAT_TEXT[c.qualification_category] || "text-slate-700";
                          return (
                            <button
                              key={c.id}
                              data-course-event
                              onClick={(e) => { e.stopPropagation(); viewCourseDetail(c); }}
                              className={`absolute left-1 right-1 rounded border-l-[3px] ${borderColor} ${bgLight} px-1.5 py-1 text-left overflow-hidden shadow-sm hover:shadow-md hover:z-20 transition-all`}
                              style={{ top, height: heightPx }}
                              data-testid={`week-event-${c.id}`}
                            >
                              <div className={`text-[10px] font-semibold ${textColor} leading-tight truncate`}>{c.title}</div>
                              <div className="text-[9px] text-muted-foreground truncate">{c.start_time}–{c.end_time}</div>
                              <div className={`text-[9px] font-bold mt-0.5 ${isFull ? "text-red-600" : "text-emerald-700"}`}>
                                {isMultiDay ? <span>{dayCount}<span className="text-muted-foreground font-normal">/{max} today</span></span> : <>{enrolled}/{max}{isFull ? " FULL" : ""}</>}
                                {(c.walk_in_count || 0) > 0 && <span className="ml-1 text-sky-600">+{c.walk_in_count} walk-in</span>}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Month Grid View (single or 2-month side-by-side) */}
          {!isWeekMode && (() => {
            const visibleHeaders = hideWeekends
              ? weekHeaders.map((h, i) => ({ h, show: i !== 0 && i !== 6 }))
              : weekHeaders.map(h => ({ h, show: true }));
            const numCols = visibleHeaders.filter(x => x.show).length;

            const renderMonthBlock = (blockDays, blockPad, label) => (
              <div className="flex-1 min-w-0" data-testid={`month-block-${label || "main"}`}>
                {label && (
                  <div className="px-3 py-1.5 bg-slate-50 border-b border-border text-[11px] font-semibold text-foreground">{label}</div>
                )}
                <div className="grid" style={{ gridTemplateColumns: `repeat(${numCols}, minmax(0,1fr))` }}>
                  {visibleHeaders.filter(x => x.show).map(({ h }) => (
                    <div key={h} className="px-2 py-1.5 text-center text-[10px] font-medium text-muted-foreground border-b border-border">{h}</div>
                  ))}
                  {blockPad.map((_, i) => {
                    // Calendar columns are Sun..Sat. Position index of the pad slot:
                    const padIndex = i;
                    if (hideWeekends && (padIndex === 0 || padIndex === 6)) return null;
                    return <div key={`pad-${label}-${i}`} className="calendar-day border-b border-r border-border bg-slate-50/50 min-h-[100px]" />;
                  })}
                  {blockDays.map(day => {
                    if (hideWeekends && isWeekend(day)) return null;
                    const dayCourses = getCoursesForDay(day);
                    const isToday = isSameDay(day, new Date());
                    const dayIso = format(day, "yyyy-MM-dd");
                    const isSelected = selectedDays.includes(dayIso);
                    return (
                      <div
                        key={day.toISOString()}
                        onClick={(e) => {
                          if (e.target.closest('[data-course-event]')) return;
                          if (!isStaff) return;
                          if (e.shiftKey || e.metaKey || e.ctrlKey || selectedDays.length > 0) {
                            toggleDaySelection(day, e);
                          } else {
                            openCreate(dayIso);
                          }
                        }}
                        className={`border-b border-r border-border p-1 min-h-[100px] transition-colors cursor-pointer relative group ${
                          isSelected ? "bg-sky-100 ring-2 ring-sky-500 ring-inset z-10" :
                          isToday ? "bg-sky-50/30 hover:bg-sky-50" : "hover:bg-slate-50/60"
                        }`}
                        data-testid={`cal-day-${dayIso}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className={`text-xs inline-block ${isToday ? "bg-sky-600 text-white px-1.5 py-0.5 rounded-full font-medium" : "text-muted-foreground"}`}>
                            {format(day, "d")}
                          </span>
                          {isStaff && (
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleDaySelection(day, e); }}
                              className={`opacity-0 group-hover:opacity-100 text-[9px] px-1 rounded ${isSelected ? "bg-sky-600 text-white opacity-100" : "bg-slate-200 text-slate-600 hover:bg-slate-300"}`}
                              title={isSelected ? "Deselect" : "Select (Shift+click for range)"}
                              data-testid={`cal-select-${dayIso}`}
                            >
                              {isSelected ? "✓" : "+"}
                            </button>
                          )}
                        </div>
                        <div className="space-y-1 mt-0.5">
                          {dayCourses.slice(0, 4).map(c => {
                            const enrolled = c.enrolled_count || 0;
                            const max = c.max_capacity || 6;
                            const isMultiDay = (c.course_days || []).length > 1;
                            const dayCount = isMultiDay ? ((c.per_day_counts || {})[dayIso] ?? enrolled) : enrolled;
                            const pct = max > 0 ? (dayCount / max) * 100 : 0;
                            const isFull = dayCount >= max;
                            const isNearlyFull = pct >= 80 && !isFull;
                            const capacityColor = isFull ? "text-red-600 bg-red-100" : isNearlyFull ? "text-amber-600 bg-amber-100" : "text-emerald-700 bg-emerald-100";
                            const borderColor = CAT_BORDER[c.qualification_category] || "border-slate-400";
                            const bgLight = CAT_BG_LIGHT[c.qualification_category] || "bg-slate-50";
                            const textColor = CAT_TEXT[c.qualification_category] || "text-slate-700";
                            return (
                              <div
                                key={c.id}
                                data-course-event
                                className={`calendar-event relative w-full text-left rounded border-l-[3px] ${borderColor} ${bgLight} px-1.5 py-1 group/event cursor-pointer`}
                                onClick={(e) => { e.stopPropagation(); viewCourseDetail(c); }}
                                data-testid={`cal-event-${c.id}`}
                              >
                                <div className="flex items-center justify-between gap-1">
                                  <span className={`text-[10px] font-medium ${textColor} truncate leading-tight`}>{c.title}</span>
                                  <span className={`text-[9px] font-bold px-1 py-0 rounded shrink-0 ${capacityColor}`} data-testid={`cal-capacity-${c.id}`} title={isMultiDay ? `${dayCount}/${max} attending this day` : `${enrolled}/${max} enrolled`}>
                                    {dayCount}/{max}{(c.walk_in_count || 0) > 0 && <span className="ml-0.5 text-sky-700">+{c.walk_in_count}</span>}
                                  </span>
                                </div>
                                {c.location && <div className="text-[9px] text-muted-foreground truncate mt-0.5">{c.location}</div>}
                                {isStaff && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleDelete(c); }}
                                    className="absolute top-0.5 right-0.5 opacity-0 group-hover/event:opacity-100 p-0.5 rounded bg-white border border-red-200 text-red-500 hover:bg-red-50 transition-opacity"
                                    title="Delete course"
                                    data-testid={`cal-delete-${c.id}`}
                                  >
                                    <Trash2 size={10} />
                                  </button>
                                )}
                              </div>
                            );
                          })}
                          {dayCourses.length > 4 && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setDayExpand({ date: dayIso, courses: dayCourses }); }}
                              className="text-[10px] text-sky-600 hover:text-sky-800 hover:underline pl-1"
                              data-testid={`cal-more-${dayIso}`}
                            >
                              +{dayCourses.length - 4} more
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );

            if (isMultiMode) {
              return (
                <div className="flex divide-x divide-border" data-testid="multi-month-grid">
                  {renderMonthBlock(days, padDays, format(currentMonth, "MMMM yyyy"))}
                  {renderMonthBlock(daysNext, padDaysNext, format(addMonths(currentMonth, 1), "MMMM yyyy"))}
                </div>
              );
            }
            return renderMonthBlock(days, padDays, null);
          })()}

          {/* Trainer Availability Strip */}
          <div className="border-t border-border" data-testid="trainer-availability-strip">
            <div className="p-3 bg-slate-50/50 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <User size={14} className="text-muted-foreground" />
                <span className="text-xs font-medium text-foreground">Trainer Availability</span>
              </div>
              <div className="flex items-center gap-3 text-[10px]">
                <span className="flex items-center gap-1"><span className="w-3 h-2 rounded-sm bg-emerald-100" />Free</span>
                <span className="flex items-center gap-1"><span className="w-3 h-2 rounded-sm bg-red-400" />Teaching</span>
                <span className="flex items-center gap-1"><span className="w-3 h-2 rounded-sm bg-slate-500" />Off / Holiday</span>
                {isStaff && <span className="text-muted-foreground italic hidden sm:inline">· click a day to toggle</span>}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[10px]">
                <thead>
                  <tr className="border-b border-border">
                    <th className="px-3 py-1.5 text-left font-medium text-muted-foreground sticky left-0 bg-white z-10 w-[120px] min-w-[120px]">Trainer</th>
                    {days.map(day => (
                      <th key={day.toISOString()} className={`px-0 py-1.5 text-center font-normal min-w-[28px] ${isSameDay(day, new Date()) ? "bg-sky-50" : ""} ${getDay(day) === 0 || getDay(day) === 6 ? "text-slate-300" : "text-muted-foreground"}`}>
                        {format(day, "d")}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {trainers.map(t => {
                    const busyDays = new Set();
                    const unavailDays = new Set();
                    [...(t.upcoming || []), ...(t.past || [])].forEach(c => {
                      try {
                        let d = parseISO(c.start_date);
                        const end = parseISO(c.end_date);
                        while (d <= end) {
                          if (isSameMonth(d, currentMonth)) busyDays.add(format(d, "yyyy-MM-dd"));
                          d = new Date(d.getTime() + 86400000);
                        }
                      } catch { /* ignore invalid date */ }
                    });
                    (t.unavailability || []).forEach(u => {
                      try {
                        let d = parseISO(u.start_date);
                        const end = parseISO(u.end_date);
                        while (d <= end) {
                          if (isSameMonth(d, currentMonth)) unavailDays.add(format(d, "yyyy-MM-dd"));
                          d = new Date(d.getTime() + 86400000);
                        }
                      } catch { /* ignore invalid date */ }
                    });
                    return (
                      <tr key={t.id || t.name} className="border-b border-border">
                        <td className="px-3 py-1 font-medium text-foreground sticky left-0 bg-white z-10 whitespace-nowrap">{t.name}</td>
                        {days.map(day => {
                          const dayStr = format(day, "yyyy-MM-dd");
                          const isBusy = busyDays.has(dayStr);
                          const isUnavail = unavailDays.has(dayStr);
                          const isWeekend = getDay(day) === 0 || getDay(day) === 6;
                          return (
                            <td key={dayStr} className={`px-0 py-1 text-center ${isSameDay(day, new Date()) ? "bg-sky-50" : ""}`}>
                              {isWeekend ? (
                                <span className="block w-full h-4 bg-slate-50 rounded-sm" />
                              ) : (
                                <button
                                  onClick={() => isStaff && toggleTrainerDay(t, dayStr, isBusy, isUnavail)}
                                  disabled={!isStaff || isBusy}
                                  className={`block w-full h-4 rounded-sm transition-all ${
                                    isBusy ? "bg-red-400 cursor-not-allowed" :
                                    isUnavail ? "bg-slate-500 hover:bg-slate-600" :
                                    "bg-emerald-100 hover:bg-emerald-300 hover:ring-1 hover:ring-emerald-500"
                                  } ${isStaff && !isBusy ? "cursor-pointer" : ""}`}
                                  title={
                                    isBusy ? `${t.name} - Teaching (edit course to change)` :
                                    isUnavail ? `${t.name} - Off / Holiday (click to mark Free)` :
                                    `${t.name} - Available (click to mark Off)`
                                  }
                                  data-testid={`trainer-cell-${t.id}-${dayStr}`}
                                />
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* List View */}
      {view === "list" && (
        <div className="border border-border rounded-md bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="courses-table">
            <thead><tr className="border-b border-border text-left">
              <th className="px-4 py-2.5 font-medium text-muted-foreground text-xs">Course</th>
              <th className="px-4 py-2.5 font-medium text-muted-foreground text-xs hidden md:table-cell">Qualification</th>
              <th className="px-4 py-2.5 font-medium text-muted-foreground text-xs">Date</th>
              <th className="px-4 py-2.5 font-medium text-muted-foreground text-xs hidden lg:table-cell">Location</th>
              <th className="px-4 py-2.5 font-medium text-muted-foreground text-xs">Enrolled</th>
              <th className="px-4 py-2.5 font-medium text-muted-foreground text-xs">Status</th>
              {isStaff && <th className="px-4 py-2.5 font-medium text-muted-foreground text-xs w-28">Actions</th>}
            </tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Loading...</td></tr>
              ) : courses.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No courses found</td></tr>
              ) : courses.map((c, i) => (
                <tr key={c.id} className="border-b border-border hover:bg-slate-50/50 table-row-animate" style={{ animationDelay: `${i * 30}ms` }}>
                  <td className="px-4 py-2.5 font-medium">{c.title}</td>
                  <td className="px-4 py-2.5 text-muted-foreground hidden md:table-cell">{c.qualification_name}</td>
                  <td className="px-4 py-2.5 text-sm">{c.start_date} to {c.end_date}</td>
                  <td className="px-4 py-2.5 text-muted-foreground hidden lg:table-cell">{c.location || "—"}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <span className={`font-bold ${(c.enrolled_count || 0) >= (c.max_capacity || 6) ? "text-red-600" : (c.enrolled_count || 0) >= (c.max_capacity || 6) * 0.8 ? "text-amber-600" : "text-emerald-600"}`}>{c.enrolled_count || 0}</span>
                      <span className="text-muted-foreground">/ {c.max_capacity}</span>
                      <div className="w-12 h-1.5 bg-slate-100 rounded-full overflow-hidden hidden sm:block">
                        <div className={`h-full rounded-full ${(c.enrolled_count || 0) >= (c.max_capacity || 6) ? "bg-red-500" : (c.enrolled_count || 0) >= (c.max_capacity || 6) * 0.8 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(((c.enrolled_count || 0) / (c.max_capacity || 6)) * 100, 100)}%` }} />
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2.5"><Badge variant="outline" className={`text-[10px] capitalize ${STATUS_COLORS[c.status] || ""}`}>{c.status?.replace("_", " ")}</Badge></td>
                  {isStaff && (
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1">
                        <button onClick={() => viewCourseDetail(c)} className="p-1.5 rounded hover:bg-slate-100" title="View details" data-testid={`view-course-${c.id}`}><Users size={14} /></button>
                        <button onClick={() => openEnroll(c)} className="p-1.5 rounded hover:bg-slate-100" title="Enroll member" data-testid={`enroll-course-${c.id}`}><UserPlus size={14} /></button>
                        <button onClick={() => handleDuplicate(c)} className="p-1.5 rounded hover:bg-slate-100" title="Duplicate course" data-testid={`dup-course-${c.id}`}><Copy size={14} /></button>
                        <button onClick={() => openEdit(c)} className="p-1.5 rounded hover:bg-slate-100" data-testid={`edit-course-${c.id}`}><Pencil size={14} /></button>
                        <button onClick={() => handleDelete(c)} className="p-1.5 rounded hover:bg-red-50 text-red-500" data-testid={`delete-course-${c.id}`}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Trainers View */}
      {view === "trainers" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-heading font-semibold text-sm">Trainer Management</h3>
              <p className="text-xs text-muted-foreground">{trainers.length} trainer{trainers.length !== 1 ? "s" : ""} registered</p>
            </div>
            {isStaff && (
              <Button onClick={openCreateTrainer} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-trainer-btn">
                <Plus size={16} className="mr-1" /> Add Trainer
              </Button>
            )}
          </div>
          <div className="border border-border rounded-md bg-white" data-testid="trainers-section">
            {trainers.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground text-sm">No trainers yet. Add your first trainer to get started.</div>
            ) : (
              <div className="divide-y divide-border">
                {trainers.map((t, i) => (
                  <div key={t.id || t.name} className="p-4 animate-fade-in" style={{ animationDelay: `${i * 40}ms` }}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center text-sm font-bold text-slate-600">{t.name?.[0]}</div>
                        <div>
                          <p className="font-medium text-sm">{t.name}</p>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            {t.email && <span>{t.email}</span>}
                            {t.phone && <span>{t.phone}</span>}
                          </div>
                          {(t.specializations || []).length > 0 && (
                            <div className="flex gap-1 mt-1 flex-wrap">
                              {t.specializations.map(s => <Badge key={s} variant="outline" className="text-[9px] px-1.5 py-0">{s}</Badge>)}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={`text-[10px] ${t.upcoming_count > 0 ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-emerald-100 text-emerald-700 border-emerald-200"}`}>
                          {t.upcoming_count > 0 ? `${t.upcoming_count} upcoming` : "Available"}
                        </Badge>
                        {t.certificate_count > 0 && (
                          <Select onValueChange={() => { /* no-op: dropdown is informational */ }}>
                            <SelectTrigger className="h-7 text-[11px] w-[200px]" data-testid={`trainer-certs-${t.id}`}>
                              <SelectValue placeholder={`${t.certificate_count} certificate${t.certificate_count === 1 ? "" : "s"}`} />
                            </SelectTrigger>
                            <SelectContent>
                              {(t.certificates || []).map(cert => (
                                <SelectItem key={cert.id} value={cert.id} disabled className="opacity-100 cursor-default">
                                  <div className="flex items-center gap-2">
                                    <span className={`w-1.5 h-1.5 rounded-full ${cert.computed_status === "expired" ? "bg-red-500" : cert.computed_status === "expiring_soon" ? "bg-amber-500" : "bg-emerald-500"}`} />
                                    <span className="text-xs">{cert.qualification_name}</span>
                                    <span className="text-[10px] text-muted-foreground">· exp {cert.expiry_date}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        {isStaff && (
                          <div className="flex gap-1">
                            <button onClick={() => openUnavail(t)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title="Mark unavailable" data-testid={`unavail-trainer-${t.id}`}><CalendarX2 size={14} /></button>
                            <button onClick={() => openEditTrainer(t)} className="p-1.5 rounded hover:bg-slate-100" data-testid={`edit-trainer-${t.id}`}><Pencil size={14} /></button>
                            <button onClick={() => handleDeleteTrainer(t)} className="p-1.5 rounded hover:bg-red-50 text-red-500" data-testid={`delete-trainer-${t.id}`}><Trash2 size={14} /></button>
                          </div>
                        )}
                      </div>
                    </div>
                    {t.upcoming?.length > 0 && (
                      <div className="ml-12 space-y-1 mt-1">
                        {t.upcoming.map(c => (
                          <div key={c.id} className="flex items-center justify-between text-xs p-2 rounded border border-border bg-slate-50/50">
                            <div className="flex items-center gap-3">
                              <span className="font-medium text-foreground">{c.title}</span>
                              <span className="text-muted-foreground">{c.start_date} to {c.end_date}</span>
                              <span className="text-muted-foreground">{c.location}</span>
                            </div>
                            <span className={`font-bold ${c.enrolled_count >= c.max_capacity ? "text-red-600" : "text-emerald-600"}`}>{c.enrolled_count}/{c.max_capacity}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {(t.unavailability || []).length > 0 && (
                      <div className="ml-12 space-y-1 mt-1">
                        {t.unavailability.map(u => (
                          <div key={u.id} className="flex items-center justify-between text-xs p-2 rounded border border-slate-300 bg-slate-100">
                            <div className="flex items-center gap-3">
                              <CalendarX2 size={12} className="text-slate-500" />
                              <span className="font-medium text-slate-600">{u.reason}</span>
                              <span className="text-slate-500">{u.start_date} to {u.end_date}</span>
                            </div>
                            {isStaff && <button onClick={() => handleRemoveUnavail(u.id)} className="p-1 rounded hover:bg-red-50 text-red-400 hover:text-red-600" data-testid={`remove-unavail-${u.id}`}><Trash2 size={12} /></button>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Staff Calendar View */}

      {/* Trainer Add/Edit Dialog */}
      <Dialog open={trainerDialogOpen} onOpenChange={setTrainerDialogOpen}>
        <DialogContent className="max-w-md" data-testid="trainer-form-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editingTrainer ? "Edit Trainer" : "Add Trainer"}</DialogTitle></DialogHeader>
          <form onSubmit={handleTrainerSubmit} className="space-y-3">
            <div><Label className="text-xs">Name</Label><Input value={trainerForm.name} onChange={(e) => setTrainerForm({ ...trainerForm, name: e.target.value })} required data-testid="trainer-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Email</Label><Input type="email" value={trainerForm.email} onChange={(e) => setTrainerForm({ ...trainerForm, email: e.target.value })} data-testid="trainer-email-input" /></div>
              <div><Label className="text-xs">Phone</Label><Input value={trainerForm.phone} onChange={(e) => setTrainerForm({ ...trainerForm, phone: e.target.value })} data-testid="trainer-phone-input" /></div>
            </div>
            <div><Label className="text-xs">Specialisations (comma separated)</Label><Input value={(trainerForm.specializations || []).join(", ")} onChange={(e) => setTrainerForm({ ...trainerForm, specializations: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} placeholder="Domestic Gas, Electrical, Oil" data-testid="trainer-specs-input" /></div>
            <div><Label className="text-xs">Notes</Label><Input value={trainerForm.notes} onChange={(e) => setTrainerForm({ ...trainerForm, notes: e.target.value })} data-testid="trainer-notes-input" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTrainerDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="trainer-submit-btn">{editingTrainer ? "Update" : "Add Trainer"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Unavailability Dialog */}
      <Dialog open={unavailOpen} onOpenChange={setUnavailOpen}>
        <DialogContent className="max-w-sm" data-testid="unavail-dialog">
          <DialogHeader><DialogTitle className="font-heading">Mark Unavailable</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Trainer: <strong>{unavailTrainer?.name}</strong></p>
          <form onSubmit={handleAddUnavail} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">From</Label><Input type="date" value={unavailForm.start_date} onChange={(e) => setUnavailForm({ ...unavailForm, start_date: e.target.value })} required data-testid="unavail-start-date" /></div>
              <div><Label className="text-xs">To</Label><Input type="date" value={unavailForm.end_date} onChange={(e) => setUnavailForm({ ...unavailForm, end_date: e.target.value })} required data-testid="unavail-end-date" /></div>
            </div>
            <div>
              <Label className="text-xs">Reason</Label>
              <Select value={unavailForm.reason} onValueChange={(v) => setUnavailForm({ ...unavailForm, reason: v })}>
                <SelectTrigger data-testid="unavail-reason-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Holiday">Holiday</SelectItem>
                  <SelectItem value="Sick">Sick Leave</SelectItem>
                  <SelectItem value="Personal">Personal</SelectItem>
                  <SelectItem value="Training">Training / CPD</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setUnavailOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="unavail-submit-btn">Add Unavailability</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Mark Unavailable Dialog (from calendar multi-select) */}
      <Dialog open={bulkUnavailOpen} onOpenChange={setBulkUnavailOpen}>
        <DialogContent className="max-w-sm" data-testid="bulk-unavail-dialog">
          <DialogHeader><DialogTitle className="font-heading">Mark Trainer Unavailable</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            <strong>{selectedDays.length}</strong> day{selectedDays.length === 1 ? "" : "s"} selected
            {selectedDays.length > 0 && <span className="block text-xs mt-1">{selectedDays[0]} → {selectedDays[selectedDays.length - 1]}</span>}
          </p>
          <form onSubmit={submitBulkUnavail} className="space-y-3">
            <div>
              <Label className="text-xs">Trainer</Label>
              <Select value={bulkUnavailForm.trainer_id} onValueChange={(v) => setBulkUnavailForm({ ...bulkUnavailForm, trainer_id: v })}>
                <SelectTrigger data-testid="bulk-unavail-trainer-select"><SelectValue placeholder="Select trainer..." /></SelectTrigger>
                <SelectContent>
                  {trainers.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Reason</Label>
              <Select value={bulkUnavailForm.reason} onValueChange={(v) => setBulkUnavailForm({ ...bulkUnavailForm, reason: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Holiday">Holiday</SelectItem>
                  <SelectItem value="Sick">Sick Leave</SelectItem>
                  <SelectItem value="Personal">Personal</SelectItem>
                  <SelectItem value="Training">Training / CPD</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setBulkUnavailOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 text-white" disabled={!bulkUnavailForm.trainer_id} data-testid="bulk-unavail-submit-btn">
                Mark Unavailable
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="course-form-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Course" : "Add Course"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div><Label className="text-xs">Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="course-title-input" /></div>
            <div>
              <Label className="text-xs">Qualification</Label>
              <SearchableSelect
                options={quals.map(q => ({ value: q.id, label: q.name, hint: q.category }))}
                value={form.qualification_id}
                onChange={(v) => {
                  const q = quals.find(x => x.id === v);
                  const month = form.start_date ? format(parseISO(form.start_date), "MMMM") : format(new Date(), "MMMM");
                  const autoTitle = q ? `${q.name} – ${month}` : form.title;
                  setForm({
                    ...form,
                    qualification_id: v,
                    ...(q && (!form.title || form.title.includes("–")) ? { title: autoTitle } : {}),
                    ...(q && !editing ? { price_per_student: q.default_price || form.price_per_student, vat_applicable: q.default_vat_applicable ?? form.vat_applicable } : {}),
                  });
                }}
                placeholder="Select qualification"
                searchPlaceholder="Search qualifications…"
                testId="course-qual-select"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Start Date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} required data-testid="course-start-date" /></div>
              <div><Label className="text-xs">End Date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} required data-testid="course-end-date" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Start Time</Label>
                <Input type="time" step="1800" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} required data-testid="course-start-time" />
              </div>
              <div>
                <Label className="text-xs">End Time</Label>
                <Input type="time" step="1800" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} required data-testid="course-end-time" />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground -mt-1" data-testid="course-duration-hint">
              Duration: <strong>{formatDuration(form.start_time, form.end_time)}</strong>
            </p>
            <div><Label className="text-xs">Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="course-location-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Max Capacity</Label><Input type="number" min="1" value={form.max_capacity} onChange={(e) => setForm({ ...form, max_capacity: parseInt(e.target.value) || 6 })} data-testid="course-capacity-input" /></div>
              <div>
                <Label className="text-xs">Walk-in Learners</Label>
                <Input type="number" min="0" value={form.walk_in_count} onChange={(e) => setForm({ ...form, walk_in_count: parseInt(e.target.value) || 0 })} data-testid="course-walkin-input" />
                <p className="text-[10px] text-muted-foreground mt-1">Attendees not registered in the CRM (bringing docs on the day).</p>
              </div>
            </div>
            <div>
              <Label className="text-xs">Instructor</Label>
              <SearchableSelect
                options={trainers.map(t => ({ value: t.name, label: t.name, hint: t.email || "" }))}
                value={form.instructor}
                onChange={(v) => setForm({ ...form, instructor: v })}
                placeholder="Select trainer"
                searchPlaceholder="Search trainers…"
                testId="course-instructor-select"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Price per Student (£)</Label><Input type="number" min="0" step="0.01" value={form.price_per_student} onChange={(e) => setForm({ ...form, price_per_student: parseFloat(e.target.value) || 0 })} data-testid="course-price-input" /></div>
              <div><Label className="text-xs">Course Cost (£)</Label><Input type="number" min="0" step="0.01" value={form.course_cost} onChange={(e) => setForm({ ...form, course_cost: parseFloat(e.target.value) || 0 })} data-testid="course-cost-input" /></div>
            </div>
            <div className="flex items-center justify-between border border-border rounded p-2 bg-slate-50">
              <div>
                <Label className="text-xs">Apply VAT (20%)</Label>
                <p className="text-[11px] text-muted-foreground">Total per student: £{((parseFloat(form.price_per_student) || 0) * (form.vat_applicable ? 1.2 : 1)).toFixed(2)}</p>
              </div>
              <Switch checked={!!form.vat_applicable} onCheckedChange={(v) => setForm({ ...form, vat_applicable: v })} data-testid="course-vat-switch" />
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="course-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="course-submit-btn">{editing ? "Update" : "Create"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Enroll Dialog */}
      <Dialog open={enrollOpen} onOpenChange={setEnrollOpen}>
        <DialogContent className="max-w-md" data-testid="enroll-dialog">
          <DialogHeader><DialogTitle className="font-heading">Enroll Learners</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground mb-2">Course: {enrollCourse?.title}</p>
          <div>
            <Label className="text-xs">Learners <span className="text-muted-foreground font-normal">(type to search · pick multiple)</span></Label>
            <MultiSearchableSelect
              options={members.map(m => ({ value: m.id, label: m.name, hint: m.email || "(walk-in)" }))}
              value={enrollMemberIds}
              onChange={setEnrollMemberIds}
              placeholder="Select learners…"
              searchPlaceholder="Type a name or email…"
              testId="enroll-member-multiselect"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEnrollOpen(false)}>Cancel</Button>
            <Button onClick={handleEnroll} disabled={!enrollMemberIds.length} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="enroll-submit-btn">
              Enroll{enrollMemberIds.length > 1 ? ` ${enrollMemberIds.length} learners` : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Attending Days Picker */}
      <Dialog open={!!daysDialogEnrol} onOpenChange={(o) => !o && setDaysDialogEnrol(null)}>
        <DialogContent className="max-w-sm" data-testid="days-picker-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Attending days</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground -mt-1">
            <span className="font-medium text-slate-700">{daysDialogEnrol?.member_name}</span> — pick the course days they&apos;re attending.
          </p>
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {(detailCourse?.course_days || []).map((d) => {
              const checked = daysDialogPick.includes(d);
              return (
                <label key={d} className={`flex items-center justify-between px-3 py-2 rounded border cursor-pointer text-sm ${checked ? "bg-emerald-50 border-emerald-200" : "bg-white border-border hover:bg-slate-50"}`}>
                  <span>{format(parseISO(d), "EEEE, d MMMM yyyy")}</span>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleDayPick(d)}
                    data-testid={`day-pick-${d}`}
                    className="h-4 w-4"
                  />
                </label>
              );
            })}
          </div>
          <div className="text-[11px] text-muted-foreground">
            {daysDialogPick.length}/{(detailCourse?.course_days || []).length} days selected
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDaysDialogEnrol(null)}>Cancel</Button>
            <Button
              onClick={saveAttendingDates}
              disabled={!daysDialogPick.length}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="save-attending-days-btn"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MemberProfile
        memberId={profileMemberId}
        open={!!profileMemberId}
        onClose={() => { setProfileMemberId(null); refreshDetail(); fetchCourses(); }}
        onUpdate={refreshDetail}
        isAdmin={user?.role === "admin" || user?.role === "superadmin"}
        compact
      />

      {/* Course Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="course-detail-dialog">
          {detailCourse && (() => {
            const enrolled = detailCourse.enrolled_count || 0;
            const max = detailCourse.max_capacity || 6;
            const pct = max > 0 ? Math.min((enrolled / max) * 100, 100) : 0;
            const isFull = enrolled >= max;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="font-heading">{detailCourse.title}</DialogTitle>
                  <DialogDescription className="sr-only">Course details, enrolled members, and attendance.</DialogDescription>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[detailCourse.status] || ""} capitalize`}>{detailCourse.status?.replace("_", " ")}</Badge>
                    <Badge variant="outline" className="text-[10px]">{detailCourse.qualification_category}</Badge>
                  </div>
                </DialogHeader>

                {/* Course info grid */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-start gap-2 text-muted-foreground min-w-0">
                    <Clock size={14} className="mt-0.5 shrink-0" />
                    <span className="break-words min-w-0">{detailCourse.start_date} to {detailCourse.end_date}</span>
                  </div>
                  <div className="flex items-start gap-2 text-muted-foreground min-w-0">
                    <MapPin size={14} className="mt-0.5 shrink-0" />
                    <span className="break-words min-w-0">{detailCourse.location || "TBC"}</span>
                  </div>
                  <div className="flex items-start gap-2 text-muted-foreground min-w-0">
                    <User size={14} className="mt-0.5 shrink-0" />
                    <span className="break-words min-w-0">Instructor: {detailCourse.instructor || "TBC"}</span>
                  </div>
                  <div className="flex items-start gap-2 text-muted-foreground min-w-0">
                    <GraduationCap size={14} className="mt-0.5 shrink-0" />
                    <span className="break-words min-w-0">{detailCourse.qualification_name}</span>
                  </div>
                </div>

                {/* Capacity bar */}
                <div className="border border-border rounded-md p-3" data-testid="course-capacity-detail">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-foreground">Capacity</span>
                    <span className={`text-sm font-bold ${isFull ? "text-red-600" : pct >= 80 ? "text-amber-600" : "text-emerald-600"}`}>
                      {enrolled} / {max} {isFull && "(FULL)"}
                    </span>
                  </div>
                  <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-300 ${isFull ? "bg-red-500" : pct >= 80 ? "bg-amber-500" : "bg-emerald-500"}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="text-[10px] text-muted-foreground">{max - enrolled} space{max - enrolled !== 1 ? "s" : ""} remaining</span>
                    <span className="text-[10px] text-muted-foreground">{Math.round(pct)}% filled</span>
                  </div>
                  {(detailCourse.walk_in_count || 0) > 0 && (
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-border" data-testid="course-walkins-detail">
                      <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                        <UserPlus size={11} /> Walk-in learners (not in CRM)
                      </span>
                      <span className="text-sm font-bold text-sky-600">+{detailCourse.walk_in_count}</span>
                    </div>
                  )}
                </div>

                {/* Enrolled members list */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs font-medium text-foreground flex items-center gap-1"><Users size={14} /> Enrolled Members</h4>
                    {isStaff && !isFull && (
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setDetailOpen(false); openEnroll(detailCourse); }} data-testid="detail-enroll-btn">
                        <UserPlus size={12} className="mr-1" /> Add
                      </Button>
                    )}
                  </div>
                  {(detailCourse.enrollments || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-3">No members enrolled yet</p>
                  ) : (
                    <div className="space-y-1">
                      {detailCourse.enrollments.map((enr, i) => {
                        const totalDays = (detailCourse.course_days || []).length;
                        const attDays = (enr.attending_dates || detailCourse.course_days || []).length;
                        const partial = totalDays > 1 && attDays < totalDays;
                        return (
                          <div key={enr.id} className="animate-fade-in" style={{ animationDelay: `${i * 30}ms` }}>
                          <div className="flex items-center justify-between gap-2 py-1.5 px-2 rounded border border-border text-sm">
                            <div className="min-w-0 flex-1">
                              {enr.member_id ? (
                                <button type="button" onClick={() => setProfileMemberId(enr.member_id)} className="block max-w-full font-medium text-foreground truncate text-left hover:text-sky-700 hover:underline underline-offset-2 transition-colors" title="Open member profile" data-testid={`enr-member-link-${enr.id}`}>{enr.member_name}</button>
                              ) : (
                                <div className="font-medium text-foreground truncate">{enr.member_name}</div>
                              )}
                              <div className="text-muted-foreground text-xs truncate">{enr.member_email || <span className="italic">walk-in</span>}</div>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                              {totalDays > 1 && isStaff && (
                                <button
                                  onClick={() => openDaysDialog(enr)}
                                  className={`text-[10px] px-2 py-0.5 rounded border whitespace-nowrap transition ${partial ? "bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100" : "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"}`}
                                  title="Set attending days"
                                  data-testid={`enr-days-${enr.id}`}
                                >
                                  <CalendarDays size={10} className="inline -mt-px mr-0.5" />
                                  {attDays}/{totalDays}
                                </button>
                              )}
                              <Badge variant="outline" className="text-[9px] capitalize whitespace-nowrap">{enr.status}</Badge>
                              {isStaff && (
                                <button
                                  onClick={() => { setEditingNoteFor(enr.id); setNoteDraft(enr.notes || ""); }}
                                  className={`p-1 rounded hover:bg-slate-100 ${enr.notes ? "text-sky-600" : "text-slate-400"}`}
                                  title={enr.notes ? `Note: ${enr.notes}` : "Add a note (course variant / exam type / etc.)"}
                                  data-testid={`enr-note-${enr.id}`}
                                >
                                  <Pencil size={11} />
                                </button>
                              )}
                              {isStaff && (
                                <button onClick={() => handleRemoveEnrollment(detailCourse.id, enr.id)} className="p-1 rounded hover:bg-red-50 text-red-400 hover:text-red-600 shrink-0" data-testid={`remove-enr-${enr.id}`}>
                                  <Trash2 size={12} />
                                </button>
                              )}
                            </div>
                          </div>
                          {editingNoteFor === enr.id && (
                            <div className="w-full mt-1" data-testid={`enr-note-editor-${enr.id}`}>
                              <div className="flex items-center gap-1">
                                <Input
                                  value={noteDraft}
                                  onChange={(e) => setNoteDraft(e.target.value)}
                                  placeholder="e.g. CCN1 only, resit, F-Gas Cat II"
                                  className="h-7 text-xs"
                                  autoFocus
                                />
                                <Button size="sm" className="h-7 text-xs bg-slate-900 hover:bg-slate-800 text-white" onClick={async () => {
                                  try {
                                    await api.put(`/courses/${detailCourse.id}/enrollments/${enr.id}`, { notes: noteDraft });
                                    toast.success("Note saved");
                                    setEditingNoteFor(null);
                                    // Patch the detail view in place so no reload is needed.
                                    setDetailCourse(prev => prev ? ({
                                      ...prev,
                                      enrollments: (prev.enrollments || []).map(x => x.id === enr.id ? { ...x, notes: noteDraft } : x),
                                    }) : prev);
                                  } catch (err) { toast.error("Failed to save note"); }
                                }} data-testid={`enr-note-save-${enr.id}`}>Save</Button>
                                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditingNoteFor(null)}>Cancel</Button>
                              </div>
                            </div>
                          )}
                          {enr.notes && editingNoteFor !== enr.id && (
                            <p className="w-full text-[11px] text-sky-700 bg-sky-50 border border-sky-100 rounded px-2 py-1 mt-1 italic" data-testid={`enr-note-text-${enr.id}`}>{enr.notes}</p>
                          )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Per-day capacity breakdown (multi-day courses only) */}
                {(detailCourse.course_days || []).length > 1 && (
                  <div data-testid="per-day-breakdown">
                    <h4 className="text-xs font-medium text-foreground mb-2 flex items-center gap-1">
                      <CalendarDays size={12} /> Daily attendance
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-1.5">
                      {(detailCourse.course_days || []).map((d) => {
                        const count = (detailCourse.per_day_counts || {})[d] || 0;
                        const cap = detailCourse.max_capacity || 6;
                        const pct = Math.min((count / cap) * 100, 100);
                        const tone = count >= cap ? "text-rose-700 border-rose-200 bg-rose-50"
                                   : count >= cap * 0.8 ? "text-amber-700 border-amber-200 bg-amber-50"
                                   : "text-emerald-700 border-emerald-200 bg-emerald-50";
                        return (
                          <div key={d} className={`rounded border px-2 py-1.5 ${tone}`} data-testid={`day-count-${d}`}>
                            <div className="text-[10px] uppercase tracking-wide opacity-80">{format(parseISO(d), "EEE d MMM")}</div>
                            <div className="text-sm font-bold">{count}<span className="text-[10px] font-normal opacity-70">/{cap}</span></div>
                            <div className="h-1 mt-0.5 rounded-full bg-white/60 overflow-hidden">
                              <div className="h-full bg-current opacity-60" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Waiting List */}
                {isFull && (
                  <div data-testid="waitlist-section">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs font-medium text-foreground flex items-center gap-1"><Clock size={14} /> Waiting List</h4>
                      {isStaff && (
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setDetailOpen(false); openEnroll(detailCourse); }} data-testid="detail-waitlist-btn">
                          <UserPlus size={12} className="mr-1" /> Add to Waitlist
                        </Button>
                      )}
                    </div>
                    {(detailCourse.waitlist || []).length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-2 border border-dashed border-border rounded-md">No one on the waiting list</p>
                    ) : (
                      <div className="space-y-1">
                        {detailCourse.waitlist.map((w, i) => (
                          <div key={w.id} className="flex items-center justify-between gap-2 py-1.5 px-2 rounded border border-amber-200 bg-amber-50 text-sm">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <span className="text-[10px] font-bold text-amber-600 w-5 shrink-0">#{i + 1}</span>
                              <span className="font-medium truncate">{w.member_name}</span>
                              <span className="text-xs text-muted-foreground truncate">{w.member_email}</span>
                            </div>
                            {isStaff && (
                              <button onClick={() => handleRemoveFromWaitlist(detailCourse.id, w.id)} className="p-1 rounded hover:bg-red-50 text-red-400 shrink-0" data-testid={`remove-wl-${w.id}`}>
                                <Trash2 size={12} />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Action buttons */}
                {isStaff && (
                  <DialogFooter className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => { handleDuplicate(detailCourse); setDetailOpen(false); }} data-testid="detail-dup-btn">
                      <Copy size={14} className="mr-1" /> Duplicate
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setDetailOpen(false); openEdit(detailCourse); }} data-testid="detail-edit-btn">
                      <Pencil size={14} className="mr-1" /> Edit Course
                    </Button>
                  </DialogFooter>
                )}
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Day-expand dialog — opens when a user clicks "+N more" on a
          busy calendar day. Lists every course that day so you can jump
          to any one of them without accidentally creating a new course. */}
      <Dialog open={!!dayExpand} onOpenChange={(open) => !open && setDayExpand(null)}>
        <DialogContent className="max-w-md" data-testid="day-expand-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Courses on {dayExpand?.date && format(parseISO(dayExpand.date), "EEEE, dd MMM yyyy")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-1 max-h-[60vh] overflow-y-auto">
            {(dayExpand?.courses || []).map(c => {
              const enrolled = c.enrolled_count || 0;
              const max = c.max_capacity || 6;
              const isFull = enrolled >= max;
              const borderColor = CAT_BORDER[c.qualification_category] || "border-slate-400";
              const bgLight = CAT_BG_LIGHT[c.qualification_category] || "bg-slate-50";
              const textColor = CAT_TEXT[c.qualification_category] || "text-slate-700";
              return (
                <button
                  key={c.id}
                  onClick={() => { setDayExpand(null); viewCourseDetail(c); }}
                  className={`w-full text-left rounded border-l-[3px] ${borderColor} ${bgLight} px-3 py-2 hover:brightness-95 transition`}
                  data-testid={`day-expand-course-${c.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-sm font-medium ${textColor}`}>{c.title}</span>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${isFull ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                      {enrolled}/{max}{isFull ? " FULL" : ""}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-[11px] text-muted-foreground">
                    {c.start_time && <span><Clock size={10} className="inline -mt-px mr-0.5" />{c.start_time}–{c.end_time}</span>}
                    {c.location && <span className="truncate"><MapPin size={10} className="inline -mt-px mr-0.5" />{c.location}</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete-course confirmation (replaces window.confirm which is
          blocked inside many iframe sandboxes — fixes the "delete button
          appears but does not do anything" bug). */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-course-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this course?</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to permanently remove <strong>{deleteTarget?.title}</strong>
              {deleteTarget?.start_date ? ` (${deleteTarget.start_date})` : ""}.
              All enrolments on this course will also be removed. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-course-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-course-confirm"
            >
              Delete course
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
